import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import styled from "styled-components";
import { operationType, selectDataType } from "../../../../services/types";
import { Select } from "../../../form/select";
import { updateNodeValue } from "../../../../services/method";
import { Textarea } from "../../../form/textarea";
import { TextInput } from "../../../form/textInput";
import { SettingsBlock, settingsDataType } from "../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../expanded";

type RoutingProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function Routing({ id, data, setChildMinimize, setChildSettings }: RoutingProps) {
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'Let LLM Decide (Array)', refId: 'llm_decide', isActive: true },
      { id: 8, name: 'Jev decide', refId: 'jev_decide' },
      { id: 2, name: 'Greater than', refId: 'greater_than' }, { id: 3, name: 'Less than', refId: 'less_than' }, 
      { id: 4, name: 'Equal', refId: 'equal'}, { id: 5, name: 'Not equal', refId: 'not_equal' }, 
      { id: 6, name: 'Greater than or equal to', refId: 'greater_equal' }, { id: 7, name: 'Less than or equal to', refId: 'less_equal' }, 
   ]);
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === (data.value?.request || requests[0].refId )})));
      if (!data.value?.request) updateNodeValue(id, setNodes, { request: requests[0].refId });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const handleSelectRequest = (selectId: number) => {
      setRequests((requests) => requests.map((request) => {
         if (request.id === selectId) return { ...request, isActive: true };
         else return { ...request, isActive: false };
      }));
      const selected = requests.find(obj => obj.id === selectId)?.refId;
      const patch: Record<string, unknown> = { request: selected };
      if (selected === 'jev_decide' && (data.value?.threshold === undefined || data.value?.threshold === '')) {
         patch.threshold = '0.8';
      }
      updateNodeValue(id, setNodes, patch);
   }
   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   }

   // ---
   const isWarning = false;

   return (<>
      <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            style={{ minWidth: '350px' }}
         ><Styled>
            <Select data={ requests } handleSelectItem={ handleSelectRequest } isSmall={ true } />
            <Textarea keyRef="value" value={ data.value?.value || '' } placeholder={data.value?.request === 'llm_decide' ? 'Use an array of values that you want to compare with the input.' : data.value?.request === 'jev_decide' ? 'One class per line. The chosen label is the output.\ncharged twice\nrefund request\npassword reset' : 'value'} handleChange={ handleChangeInput } />
            {data.value?.request === 'jev_decide' ? (
               <TextInput
                  keyRef="threshold"
                  title="True if confidence is at least"
                  value={data.value?.threshold === undefined || data.value?.threshold === '' ? '0.8' : String(data.value.threshold)}
                  placeholder="0.8"
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  handleChange={handleChangeInput}
               />
            ) : null}
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}

const Main = styled.div`
   min-width: 150px;
   padding: 0;
`;

export const Styled = styled.div`
   .textarea {
      margin-top: 10px;

      textarea { 
         padding: 7.5px;
         font-size: 15px;
         min-height: 80px;
      }
   }
   .textInput.threshold {
      margin-top: 12px;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 6px;

      h4 {
         width: auto;
         max-width: none;
         white-space: normal;
         line-height: 1.35;
         margin: 0;
         font-size: 13px;
         color: #4b5563;
      }

      > div {
         width: 120px;
         max-width: 100%;
      }
   }
`;